import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import LogoutConfirmModal from '../common/LogoutConfirmModal';
import SupportUsModal from '../common/SupportUsModal';
import { useLanguageNavigation } from '../../hooks/useLanguageNavigation';
import LocalizedLink from '../common/LocalizedLink';

const { FiMenu, FiX, FiUser, FiSettings, FiLogOut, FiLogIn, FiGlobe, FiChevronDown, FiLoader, FiHeart } = FiIcons;

// 🎯 自定義 Hook - 優化外部點擊檢測
const useClickOutside = (handler) => {
  const domNodeRef = useRef();
  
  useEffect(() => {
    const maybeHandler = (event) => {
      if (domNodeRef.current && !domNodeRef.current.contains(event.target)) {
        handler();
      }
    };
    
    document.addEventListener('mousedown', maybeHandler);
    return () => {
      document.removeEventListener('mousedown', maybeHandler);
    };
  }, [handler]);
  
  return domNodeRef;
};

// 🎯 語言切換器組件
const LanguageSwitcher = React.memo(({ isTransparent }) => {
  const { language } = useLanguage();
  const { switchLanguage } = useLanguageNavigation();
  const [isOpen, setIsOpen] = useState(false);

  const menuRef = useClickOutside(useCallback(() => {
    setIsOpen(false);
  }, []));

  const languages = useMemo(() => ({
    en: { name: 'English', short: 'EN' },
    zh: { name: '繁體中文', short: '中文' },
    ja: { name: '日本語', short: '日本語' },
  }), []);
  
  const buttonColorClass = useMemo(() => 
    isTransparent 
      ? 'text-white hover:text-white' 
      : 'text-gray-600 hover:text-primary-600'
  , [isTransparent]);

  const handleLanguageChange = useCallback((newLang) => {
    switchLanguage(newLang);
    setIsOpen(false);
  }, [switchLanguage]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 transition-colors rounded-lg hover:bg-white/10 ${buttonColorClass}`}
      >
        <SafeIcon icon={FiGlobe} className="h-4 w-4" />
        <span className={`hidden sm:inline text-sm font-medium tracking-wide ${isTransparent ? 'drop-shadow-sm' : ''}`}>
          {languages[language].short}
        </span>
        <SafeIcon icon={FiChevronDown} className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          >
            {Object.keys(languages).map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full text-left px-4 py-2.5 text-sm font-medium tracking-wide transition-colors ${
                  language === lang 
                    ? 'bg-primary-50 text-primary-600' 
                    : 'text-gray-700'
                } hover:bg-primary-50`}
              >
                {languages[lang].name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

LanguageSwitcher.displayName = 'LanguageSwitcher';

// 🎯 用戶資料菜單組件
const UserProfileMenu = React.memo(({ 
  profileMenuOpen, 
  setProfileMenuOpen, 
  isLoggingOut, 
  handleLogoutClick, 
  t, 
  fontClass 
}) => {
  const profileMenuRef = useClickOutside(useCallback(() => {
    setProfileMenuOpen(false);
  }, [setProfileMenuOpen]));

  return (
    <div className="relative" ref={profileMenuRef}>
      <button 
        onClick={() => setProfileMenuOpen(!profileMenuOpen)} 
        disabled={isLoggingOut}
        className={`h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md hover:scale-105 ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isLoggingOut ? (
          <SafeIcon icon={FiLoader} className="h-5 w-5 text-white animate-spin" />
        ) : (
          <SafeIcon icon={FiUser} className="h-5 w-5 text-white" />
        )}
      </button>
      <AnimatePresence>
        {profileMenuOpen && !isLoggingOut && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          >
            <LocalizedLink 
              to="/settings" 
              className={`flex items-center w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors ${fontClass}`}
              onClick={() => setProfileMenuOpen(false)}
            >
              <SafeIcon icon={FiSettings} className="mr-3 h-4 w-4"/> {t('navbar.settings')}
            </LocalizedLink>
            <button 
              onClick={handleLogoutClick}
              className={`flex items-center w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors ${fontClass}`}
            >
              <SafeIcon icon={FiLogOut} className="mr-3 h-4 w-4"/> {t('navbar.logout')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

UserProfileMenu.displayName = 'UserProfileMenu';

const Navbar = () => {
  const { t, language } = useLanguage();
  const { user, signOut, isLoggingOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const location = useLocation();
  
  // 🎯 修正：使用新的 currentPath
  const { currentPath } = useLanguageNavigation();
  const isHomePageAtTop = currentPath === '/' && !isScrolled && !isOpen;

  // 滾動事件處理
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 移動菜單背景鎖定
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  const navItems = useMemo(() => [
    { name: t('navbar.home'), path: '/' },
    { name: t('navbar.features'), path: '/features' },
    { name: t('navbar.blog'), path: '/blog' },
    { name: t('navbar.about'), path: '/about' },
    { name: t('navbar.contact'), path: '/contact' },
  ], [t]);

  const fontClass = useMemo(() => 
    language === 'zh' ? 'font-semibold tracking-wide' : 'font-medium'
  , [language]);

  // 🎯 修正：使用 currentPath 來判斷 active 狀態
  const isNavItemActive = useCallback((itemPath) => {
    if (itemPath === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(itemPath);
  }, [currentPath]);

  const navItemClass = useCallback((itemPath) => {
    const isActive = isNavItemActive(itemPath);
    const baseClasses = `relative transition-colors px-3 py-2 rounded-lg ${fontClass}`;
    const transparentStateClasses = `text-white drop-shadow-sm hover:bg-white/10`;
    const solidStateClasses = `text-gray-600 hover:text-primary-600 hover:bg-gray-50`;
    
    const activeTransparentClasses = `after:content-[''] after:absolute after:left-3 after:bottom-0 after:w-[calc(100%-24px)] after:h-[2px] after:bg-white after:scale-x-100`;
    const activeSolidClasses = `text-primary-600 font-semibold after:content-[''] after:absolute after:left-3 after:bottom-0 after:w-[calc(100%-24px)] after:h-[2px] after:bg-primary-600 after:scale-x-100`;
    
    const inactiveHoverClasses = `after:content-[''] after:absolute after:left-3 after:bottom-0 after:w-[calc(100%-24px)] after:h-[2px] ${isHomePageAtTop ? 'after:bg-white' : 'after:bg-primary-600'} after:transition-transform after:scale-x-0 after:origin-left hover:after:scale-x-50`;

    if (isHomePageAtTop) {
      return `${baseClasses} ${transparentStateClasses} ${isActive ? activeTransparentClasses : inactiveHoverClasses}`;
    }
    return `${baseClasses} ${solidStateClasses} ${isActive ? activeSolidClasses : inactiveHoverClasses}`;
  }, [fontClass, isHomePageAtTop, isNavItemActive]);

  const mobileNavItemClass = useCallback((itemPath) => {
    const isActive = isNavItemActive(itemPath);
    return `block px-4 py-3 text-lg rounded-lg transition-colors ${fontClass} ${isActive ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`;
  }, [fontClass, isNavItemActive]);

  const handleLogoutClick = useCallback(() => {
    setProfileMenuOpen(false);
    setIsOpen(false);
    setShowLogoutModal(true);
  }, []);

  const handleConfirmLogout = useCallback(async () => {
    await signOut(location.pathname);
    setShowLogoutModal(false);
  }, [signOut, location.pathname]);

  const handleCancelLogout = useCallback(() => {
    setShowLogoutModal(false);
  }, []);

  const handleSupportClick = useCallback(() => {
    setIsOpen(false);
    setShowSupportModal(true);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled || isOpen || currentPath !== '/' ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <LocalizedLink to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <img src="/logo.svg" alt="PetCare Logo" className="h-8 w-auto transition-transform group-hover:scale-110" />
              </div>
              <span className={`text-2xl font-bold transition-colors ${isHomePageAtTop ? 'text-white drop-shadow' : 'text-gray-800'} ${language === 'zh' ? 'tracking-wider' : ''}`}>
                {t('common.appName')}
              </span>
            </LocalizedLink>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map(item => (
                <LocalizedLink 
                  key={item.name} 
                  to={item.path}
                  className={navItemClass(item.path)}
                >
                  {item.name}
                </LocalizedLink>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* 支持我們按鈕 */}
              <button
                onClick={handleSupportClick}
                className={`hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${fontClass} ${
                  isHomePageAtTop 
                    ? 'text-white bg-pink-500/20 hover:bg-pink-500/30 border border-pink-300/30 backdrop-blur-sm' 
                    : 'text-pink-600 bg-pink-50 hover:bg-pink-100 border border-pink-200'
                }`}
              >
                <SafeIcon icon={FiHeart} className="h-4 w-4" />
                <span className="text-sm">{t('navbar.supportUs')}</span>
              </button>

              <LanguageSwitcher isTransparent={isHomePageAtTop} />
              
              <div className="hidden md:flex items-center space-x-3">
                {user ? (
                  <>
                    <LocalizedLink 
                      to="/dashboard" 
                      className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 hover:scale-105 ${fontClass} ${isHomePageAtTop ? 'text-white bg-white/10 hover:bg-white/20 border border-white/20' : 'text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200'} ${isLoggingOut ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      {t('navbar.dashboard')}
                    </LocalizedLink>
                    <UserProfileMenu 
                      profileMenuOpen={profileMenuOpen}
                      setProfileMenuOpen={setProfileMenuOpen}
                      isLoggingOut={isLoggingOut}
                      handleLogoutClick={handleLogoutClick}
                      t={t}
                      fontClass={fontClass}
                    />
                  </>
                ) : (
                  <LocalizedLink 
                    to="/auth" 
                    className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg shadow-md transition-all duration-200 transform hover:scale-105 ${fontClass} ${isHomePageAtTop ? 'bg-white text-primary-600 hover:bg-gray-200' : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800'}`}
                  >
                    <SafeIcon icon={FiLogIn} className="h-4 w-4" />
                    <span>{t('navbar.login')}</span>
                  </LocalizedLink>
                )}
              </div>
              
              {/* Mobile Menu Button */}
              <div className="md:hidden">
                <button 
                  onClick={() => setIsOpen(!isOpen)} 
                  disabled={isLoggingOut}
                  className={`p-2 rounded-lg transition-colors ${isHomePageAtTop ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'} ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoggingOut ? (
                    <SafeIcon icon={FiLoader} className="h-6 w-6 animate-spin" />
                  ) : (
                    <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && !isLoggingOut && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed top-20 left-0 right-0 bottom-0 bg-white/95 backdrop-blur-xl z-30 border-t border-gray-200/50"
          >
            <div className="mobile-menu-container p-6 flex flex-col space-y-2 h-full overflow-y-auto">
              {navItems.map(item => (
                <LocalizedLink 
                  key={item.name} 
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={mobileNavItemClass(item.path)}
                >
                  {item.name}
                </LocalizedLink>
              ))}
              
              <div className="border-t border-gray-200 my-4"></div>
              
              {/* 移動版支持我們按鈕 */}
              <button
                onClick={handleSupportClick}
                className={`flex items-center justify-center gap-2 px-4 py-3 text-lg rounded-lg transition-colors bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 ${fontClass}`}
              >
                <SafeIcon icon={FiHeart} className="h-5 w-5" />
                {t('navbar.supportUs')}
              </button>
              
              {user ? (
                <>
                  <LocalizedLink 
                    to="/dashboard" 
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors text-gray-700 hover:bg-gray-50 ${fontClass}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navbar.dashboard')}
                  </LocalizedLink>
                  <LocalizedLink 
                    to="/settings" 
                    className={`block px-4 py-3 text-lg rounded-lg transition-colors text-gray-700 hover:bg-gray-50 ${fontClass}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navbar.settings')}
                  </LocalizedLink>
                  <button 
                    onClick={handleLogoutClick}
                    className={`flex items-center w-full text-left px-4 py-3 text-lg rounded-lg text-red-600 hover:bg-red-50 transition-colors ${fontClass}`}
                  >
                    <SafeIcon icon={FiLogOut} className="mr-3 h-5 w-5"/>
                    {t('navbar.logout')}
                  </button>
                </>
              ) : (
                <LocalizedLink 
                  to="/auth" 
                  className={`w-full text-center mt-4 px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg shadow-md hover:from-primary-700 hover:to-primary-800 transition-all duration-200 ${fontClass}`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('navbar.login')}
                </LocalizedLink>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 登出確認模態框 */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        isLoggingOut={isLoggingOut}
      />

      {/* 支持我們模態框 */}
      <SupportUsModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* 智能登出覆蓋層 */}
      <AnimatePresence>
        {isLoggingOut && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-2xl p-8 shadow-2xl max-w-sm w-full mx-4"
            >
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('navbar.loggingOut')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t('navbar.redirectingToLogin')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;